import type { ReactElement } from "react"
import { Box, Typography, useTheme } from "@mui/material"
import { RoundedBox } from "@/components/conatiners/rounded-box"
import {
  getOrderWarningCalloutSx,
  ORDER_CARD_HEADER_P,
  ORDER_TYPO_BODY,
  ORDER_TYPO_SECTION_HEADER,
} from "@/utils/order-status"

export type ReorderFailureRecoveryPanelProps = {
  onBrowseMenu: () => void
  onDismiss: () => void
}

/**
 * Persistent recovery UI when reorder preparation fails on order details.
 * Presentation only — does not clear cart or retry reorder.
 */
export function ReorderFailureRecoveryPanel({
  onBrowseMenu,
  onDismiss,
}: ReorderFailureRecoveryPanelProps): ReactElement {
  const theme = useTheme()

  return (
    <RoundedBox
      role="alert"
      data-testid="reorder-failure-recovery"
      sx={{
        ...getOrderWarningCalloutSx(theme),
      }}
    >
      <Box sx={{ p: ORDER_CARD_HEADER_P }}>
        <Typography
          component="h2"
          sx={{
            ...ORDER_TYPO_SECTION_HEADER,
            m: 0,
            color: theme.palette.text.primary,
          }}
        >
          Couldn’t prepare this order for checkout
        </Typography>
        <Typography
          component="p"
          sx={{
            ...ORDER_TYPO_BODY,
            m: 0,
            mt: theme.spacing(0.75),
            color: theme.palette.text.secondary,
            fontWeight: 400,
          }}
        >
          Some items from this past order may have changed, become unavailable, or
          require updated selections. You can review your current cart or choose
          items again from the menu.
        </Typography>

        <Box
          component="button"
          type="button"
          onClick={onBrowseMenu}
          data-testid="reorder-recovery-browse-menu"
          aria-label="Browse menu"
          sx={{
            appearance: "none",
            border: 0,
            background: "transparent",
            boxShadow: "none",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            mt: theme.spacing(1),
            px: 0,
            py: 0.5,
            minHeight: 40,
            fontFamily: "inherit",
            fontSize: theme.typography.pxToRem(14),
            fontWeight: 500,
            color: theme.palette.primary.main,
            textDecoration: "underline",
            textUnderlineOffset: 3,
            textAlign: "left",
            "&:hover": {
              color: theme.palette.primary.dark,
            },
            "&:focus-visible": {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
              borderRadius: 1,
            },
          }}
        >
          Browse menu
          <Box component="span" aria-hidden sx={{ textDecoration: "none" }}>
            →
          </Box>
        </Box>

        <Box
          component="button"
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          sx={{
            appearance: "none",
            border: 0,
            background: "transparent",
            cursor: "pointer",
            display: "block",
            mt: theme.spacing(0.5),
            px: 0.5,
            py: 0.5,
            minHeight: 40,
            fontFamily: "inherit",
            fontSize: theme.typography.pxToRem(14),
            fontWeight: 500,
            color: theme.palette.text.secondary,
            textDecoration: "underline",
            textUnderlineOffset: 3,
            "&:hover": {
              color: theme.palette.text.primary,
            },
            "&:focus-visible": {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
              borderRadius: 1,
            },
          }}
        >
          Dismiss
        </Box>
      </Box>
    </RoundedBox>
  )
}
